import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import podcastsData from '../data/podcasts.json';
import { useTheme } from '../ThemeContext';

const Footer = () => {
  const { socialMedia, podcastInfo } = podcastsData;
  const { darkMode } = useTheme();

  // Select logo based on theme
  const logoSrc = darkMode
    ? '/assets/images/logo_dk_theme.svg'
    : '/assets/images/logo_lt_theme.svg';

  // Platform icons with proper styling
  const platformIcons = {
    spotify: {
      icon: 'fab fa-spotify',
      label: 'Spotify',
      color: 'hover:text-[#1DB954]',
    },
    applePodcasts: {
      icon: 'fab fa-apple',
      label: 'Apple Podcasts',
      color: 'hover:text-[#A855F7]',
    },
    amazonMusic: {
      icon: 'fab fa-amazon',
      label: 'Amazon Music',
      color: 'hover:text-[#FF9900]',
    },
    youtube: {
      icon: 'fab fa-youtube',
      label: 'YouTube',
      color: 'hover:text-[#FF0000]',
    },
    facebook: {
      icon: 'fab fa-facebook',
      label: 'Facebook',
      color: 'hover:text-[#1877F2]',
    },
    instagram: {
      icon: 'fab fa-instagram',
      label: 'Instagram',
      color: 'hover:text-[#E4405F]',
    },
    twitter: {
      icon: 'fab fa-twitter',
      label: 'Twitter',
      color: 'hover:text-[#1DA1F2]',
    },
  };

  const quickLinks = [
    { label: 'Home', to: '/' },
    { label: 'Episodes', to: '/episodes' },
    { label: 'Contact', to: '/contact' },
  ];

  // Separate podcast platforms from social media
  const podcastPlatforms = ['spotify', 'applePodcasts', 'amazonMusic'];
  const socialPlatforms = ['youtube', 'facebook', 'instagram', 'twitter'];

  return (
    <footer className="relative mt-auto">
      {/* Gradient top border */}
      <div className="h-px bg-gradient-to-r from-transparent via-accent to-transparent" />

      <div className="bg-[var(--bg-secondary)] py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {/* Brand Column */}
            <div className="lg:col-span-1">
              <Link to="/" className="inline-block mb-4">
                <img
                  src={logoSrc}
                  alt="News from the Pew"
                  className="h-12 w-auto"
                />
              </Link>
              <p className="text-[var(--text-secondary)] text-sm leading-relaxed mb-6">
                Fresh perspectives on faith, culture, and current events from a conservative Christian worldview.
              </p>
              {/* Newsletter Signup */}
              <div className="space-y-3">
                <p className="text-[var(--text-primary)] font-medium text-sm">Stay Updated</p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-1 px-3 py-2 text-sm bg-[var(--bg-tertiary)] border border-[var(--bg-elevated)] rounded-lg
                             text-[var(--text-primary)] placeholder-[var(--text-muted)]
                             focus:outline-none focus:ring-2 focus:ring-burgundy/50 focus:border-burgundy"
                  />
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-4 py-2 text-sm font-medium bg-burgundy text-white rounded-lg
                             hover:bg-burgundy-600 transition-colors"
                  >
                    Join
                  </motion.button>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-[var(--text-primary)] font-semibold mb-4 text-sm uppercase tracking-wider">
                Quick Links
              </h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-[var(--text-secondary)] hover:text-accent
                               transition-colors text-sm"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Listen On */}
            <div>
              <h4 className="text-[var(--text-primary)] font-semibold mb-4 text-sm uppercase tracking-wider">
                Listen On
              </h4>
              <ul className="space-y-3">
                {podcastPlatforms.map((platform) => {
                  const url = socialMedia[platform];
                  const info = platformIcons[platform];
                  if (!url || !info) return null;
                  return (
                    <li key={platform}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-[var(--text-secondary)] ${info.color} transition-colors text-sm inline-flex items-center gap-2`}
                      >
                        <i className={`${info.icon} w-4`} />
                        {info.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h4 className="text-[var(--text-primary)] font-semibold mb-4 text-sm uppercase tracking-wider">
                Connect
              </h4>
              <ul className="space-y-3">
                {socialPlatforms.map((platform) => {
                  const url = socialMedia[platform];
                  const info = platformIcons[platform];
                  if (!url || !info) return null;
                  return (
                    <li key={platform}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-[var(--text-secondary)] ${info.color} transition-colors text-sm inline-flex items-center gap-2`}
                      >
                        <i className={`${info.icon} w-4`} />
                        {info.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
              {/* Email */}
              <div className="mt-6 pt-4 border-t border-[var(--bg-tertiary)]">
                <a
                  href={`mailto:${podcastInfo.email}`}
                  className="text-[var(--text-secondary)] hover:text-accent
                           transition-colors text-sm"
                >
                  {podcastInfo.email}
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-[var(--bg-tertiary)]">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-[var(--text-muted)] text-sm">
                &copy; {new Date().getFullYear()} News from the Pew. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <a
                  href="/privacy"
                  className="text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors text-sm"
                >
                  Privacy
                </a>
                <a
                  href="/terms"
                  className="text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors text-sm"
                >
                  Terms
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
